import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import CoverImageUpload from '../components/blog/CoverImageUpload'
import RichTextEditor from '../components/blog/RichTextEditor'
import MetaTags from '../components/common/MetaTags'
import WheelLoader from '../components/common/WheelLoader'
import { postsService } from '../services/postsService'
import type { Post, PostInput } from '../types'

export default function PostEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = Boolean(id)

  const [formData, setFormData] = useState<PostInput>({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    status: 'draft',
  })
  const [post, setPost] = useState<Post | null>(null)
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  useEffect(() => {
    if (isEditing && id) {
      const fetchPost = async () => {
        try {
          const data = await postsService.getPost(Number(id))
          setPost(data)
          setFormData({
            title: data.title,
            slug: data.slug,
            content: data.content || '',
            excerpt: data.excerpt || '',
            status: data.status,
          })
        } catch {
          setError('Failed to load post')
        } finally {
          setLoading(false)
        }
      }
      fetchPost()
    }
  }, [id, isEditing])

  const showSuccess = (message: string) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }

    setSaving(true)
    setError('')
    try {
      let savedPost: Post
      if (isEditing && id) {
        savedPost = await postsService.updatePost(Number(id), formData)
        showSuccess('Post saved!')
      } else {
        savedPost = await postsService.createPost(formData)
        navigate(`/blog/edit/${savedPost.id}`, { replace: true })
        showSuccess('Post created!')
      }
      setPost(savedPost)
      setFormData((prev) => ({ ...prev, slug: savedPost.slug }))
    } catch {
      setError('Failed to save post')
    } finally {
      setSaving(false)
    }
  }

  const handlePublishToggle = async () => {
    if (!post) return
    setSaving(true)
    setError('')
    try {
      const updatedPost =
        post.status === 'published'
          ? await postsService.unpublishPost(post.id)
          : await postsService.publishPost(post.id)
      setPost(updatedPost)
      setFormData((prev) => ({ ...prev, status: updatedPost.status }))
      showSuccess(updatedPost.status === 'published' ? 'Post published!' : 'Post unpublished')
    } catch {
      setError('Failed to update publish status')
    } finally {
      setSaving(false)
    }
  }

  const handleContentChange = useCallback((content: string) => {
    setFormData((prev) => ({ ...prev, content }))
  }, [])

  const handleCoverImageUpload = (updatedPost: Post) => {
    setPost(updatedPost)
    showSuccess('Cover image updated!')
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <MetaTags title="Loading... | profile.work" description="Loading post" />
        <div className="text-center py-12">
          <div className="mx-auto mb-4">
            <WheelLoader width={80} />
          </div>
          <p className="text-black/60">Loading post...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <MetaTags
        title={isEditing ? 'Edit Post | profile.work' : 'New Post | profile.work'}
        description="Create or edit a blog post"
      />

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2">
          <div className="px-4 py-3 bg-black/90 backdrop-blur-xl rounded-xl shadow-lg">
            <p className="text-sm text-white flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {successMessage}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <button
          onClick={() => navigate('/blog')}
          className="text-black/60 hover:text-black flex items-center gap-1 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Blog
        </button>
        <div className="flex gap-2 w-full sm:w-auto">
          {post && (
            <button
              onClick={handlePublishToggle}
              disabled={saving}
              className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-xl transition-all disabled:opacity-50 ${
                post.status === 'published'
                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {post.status === 'published' ? 'Unpublish' : 'Publish'}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 shadow-sm"
          >
            {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 mb-6">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Cover Image */}
        <CoverImageUpload
          postId={post?.id}
          currentImageUrl={post?.cover_image_url}
          onUpload={handleCoverImageUpload}
        />

        {/* Title */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
          <input
            type="text"
            placeholder="Post Title"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full text-2xl sm:text-3xl font-bold border-0 py-5 px-6 focus:outline-none focus:ring-0 bg-transparent"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          />
        </div>

        {/* Slug & Excerpt */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-black/70 mb-1">URL Slug</label>
            <div className="flex items-center">
              <span className="text-sm text-black/40 mr-1">/blog/</span>
              <input
                type="text"
                placeholder="post-url-slug"
                value={formData.slug || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                  }))
                }
                className="flex-1 border border-gray-300 rounded-xl py-2 px-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
            <p className="text-xs text-black/40 mt-1">Leave blank to auto-generate from title</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-black/70 mb-1">Excerpt</label>
            <textarea
              placeholder="Brief summary for previews and SEO..."
              value={formData.excerpt || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, excerpt: e.target.value }))}
              rows={2}
              className="w-full border border-gray-300 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
            />
          </div>
        </div>

        {/* Rich Text Editor */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
          <RichTextEditor content={formData.content || ''} onChange={handleContentChange} />
        </div>
      </div>
    </div>
  )
}
